import { EnumHotelSearchType, HotelLocation } from "@/lib/redux/features/hotel/type";

export type GroupedDestination = {
  key: string;
  cityId?: number;
  cityName: string;
  totalHotels: number;
  imageViewUrl?: string;
  items: HotelLocation[];
  /** Parent group (city / country) selectable for search. */
  selectableLocation?: HotelLocation;
};

const slugify = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildCitySelectableLocation = (
  group: Pick<GroupedDestination, "cityId" | "cityName" | "totalHotels" | "imageViewUrl" | "items">,
): HotelLocation | undefined => {
  const cityItem = group.items.find((item) => item.searchType === EnumHotelSearchType.CITY);
  if (cityItem) return cityItem;

  const sample = group.items[0];
  const cityId =
    group.cityId ??
    sample?.cityId ??
    (sample?.searchType === EnumHotelSearchType.CITY ? sample.id : undefined);
  if (typeof cityId !== "number") return undefined;

  const cityName = sample?.cityName || group.cityName;
  const cityNameTranslate =
    sample?.cityNameTranslate || sample?.nameLang?.vi || group.cityName;
  const countryId = sample?.countryId ?? sample?.country ?? 38;

  return {
    id: cityId,
    displayName: cityName,
    localeDisplayType: cityNameTranslate,
    numsHotel: group.totalHotels,
    activeHotel: group.totalHotels,
    country: countryId,
    countryId,
    type: ["CITY", "destination"],
    searchType: EnumHotelSearchType.CITY,
    isDomestic: sample?.isDomestic ?? true,
    isActive: true,
    address: {
      countryCode: countryId,
      cityName,
      cityId,
    },
    latitude: sample?.latitude,
    longitude: sample?.longitude,
    imageViewUrl: group.imageViewUrl || sample?.cityImageViewUrl,
    cityImageViewUrl: group.imageViewUrl || sample?.cityImageViewUrl,
    nameLang: {
      vi: cityNameTranslate,
      en: cityName || cityNameTranslate,
    },
    slug: slugify(cityName || `${cityId}`),
    tagName: [],
    labelLang: cityNameTranslate,
    _id: `cities_${cityId}`,
    cityId,
    cityName,
    cityNameTranslate,
  };
};

const buildCountrySelectableLocation = (
  group: Pick<GroupedDestination, "cityId" | "cityName" | "totalHotels" | "imageViewUrl" | "items">,
): HotelLocation | undefined => {
  const countryId = group.cityId;
  if (typeof countryId !== "number") return undefined;

  const sample = group.items[0];
  const countryName = sample?.countryName || group.cityName;
  const countryNameTranslate =
    sample?.countryNameTranslate || sample?.nameLang?.vi || group.cityName;

  return {
    id: countryId,
    displayName: countryName,
    localeDisplayType: countryNameTranslate,
    numsHotel: group.totalHotels,
    activeHotel: group.totalHotels,
    country: countryId,
    countryId,
    countryName,
    countryNameTranslate,
    type: ["COUNTRY", "destination"],
    searchType: EnumHotelSearchType.COUNTRY,
    isDomestic: false,
    isActive: true,
    imageViewUrl: group.imageViewUrl || sample?.countryImageViewUrl,
    countryImageViewUrl: group.imageViewUrl,
    nameLang: {
      vi: countryNameTranslate,
      en: countryName || countryNameTranslate,
    },
    slug: slugify(countryName || `${countryId}`),
    tagName: [],
    labelLang: countryNameTranslate,
    _id: `countries_${countryId}`,
  };
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
};

const getHotelsCount = (location: HotelLocation): number => {
  const activeHotel = toNumber(location.activeHotel);
  if (typeof activeHotel === "number") return activeHotel;
  const numsHotel = toNumber(location.numsHotel);
  if (typeof numsHotel === "number") return numsHotel;
  return 0;
};

const getCityId = (location: HotelLocation): number | undefined => {
  const cityId = toNumber(location.cityId);
  if (typeof cityId === "number") return cityId;
  const addressCityId = toNumber(location.address?.cityId);
  if (typeof addressCityId === "number") return addressCityId;
  if (location.searchType === EnumHotelSearchType.CITY) {
    return toNumber(location.id);
  }
  return undefined;
};

const getCityName = (location: HotelLocation): string => {
  return (
    location.cityNameTranslate ||
    location.address?.cityNameVi ||
    location.cityName ||
    location.address?.cityName ||
    location.displayName ||
    location.nameLang?.vi ||
    location.nameLang?.en ||
    "Khác"
  );
};

const getGroupKey = (location: HotelLocation): string => {
  const cityId = getCityId(location);
  if (typeof cityId === "number") return `city_${cityId}`;
  return `name_${getCityName(location).toLowerCase()}`;
};

const getCountryGroupKey = (location: HotelLocation): string => {
  const countryId = toNumber(location.countryId) ?? toNumber(location.country);
  if (typeof countryId === "number") return `country_${countryId}`;
  return `country_${(location.countryName || "unknown").toLowerCase()}`;
};

const getCountryDisplayName = (location: HotelLocation): string =>
  location.countryNameTranslate ||
  location.countryName ||
  location.address?.countryName ||
  "Khác";

/** Tab phổ biến: quốc nội = quận/huyện theo thành phố; quốc tế = city theo country, thứ tự API. */
export const getPopularDestinationsDisplay = (
  primaryKeys: string[],
  data: Record<string, HotelLocation>,
  typeSearch: "INTERNAL" | "EXTERNAL",
): GroupedDestination[] => {
  if (typeSearch === "EXTERNAL") {
    const groups: Record<string, GroupedDestination> = {};
    const groupOrder: string[] = [];

    primaryKeys.forEach((locationId) => {
      const location = data[locationId];
      if (!location || location.searchType !== EnumHotelSearchType.CITY) return;

      const key = getCountryGroupKey(location);
      if (!groups[key]) {
        groups[key] = {
          key,
          cityId: toNumber(location.countryId) ?? toNumber(location.country),
          cityName: getCountryDisplayName(location),
          totalHotels: 0,
          imageViewUrl: location.countryImageViewUrl || location.imageViewUrl,
          items: [],
        };
        groupOrder.push(key);
      }

      groups[key].items.push(location);
      groups[key].totalHotels += getHotelsCount(location);
    });

    return groupOrder.map((key) => ({
      ...groups[key],
      selectableLocation: buildCountrySelectableLocation(groups[key]),
    }));
  }

  const groups: Record<string, GroupedDestination> = {};
  const groupOrder: string[] = [];

  primaryKeys.forEach((locationId) => {
    const location = data[locationId];
    if (!location) return;

    if (location.searchType === EnumHotelSearchType.CITY) {
      const key = getGroupKey(location);
      if (!groups[key]) {
        groups[key] = {
          key,
          cityId: getCityId(location),
          cityName: getCityName(location),
          totalHotels: getHotelsCount(location),
          imageViewUrl: location.imageViewUrl,
          items: [location],
        };
        groupOrder.push(key);
      }
      return;
    }

    if (location.searchType !== EnumHotelSearchType.AREA) return;

    const key = getGroupKey(location);
    if (!groups[key]) {
      groups[key] = {
        key,
        cityId: getCityId(location),
        cityName: getCityName(location),
        totalHotels: 0,
        imageViewUrl: location.cityImageViewUrl || location.imageViewUrl,
        items: [],
      };
      groupOrder.push(key);
    }

    groups[key].items.push(location);
    groups[key].totalHotels = Math.max(groups[key].totalHotels, getHotelsCount(location));
  });

  return groupOrder.map((key) => ({
    ...groups[key],
    selectableLocation: buildCitySelectableLocation(groups[key]),
  }));
};

const toLocationMap = (locations: HotelLocation[]) =>
  Object.fromEntries(locations.map((location) => [location._id, location]));

export const buildPopularDomesticGroups = (locations: HotelLocation[]) =>
  getPopularDestinationsDisplay(
    locations.map((location) => location._id),
    toLocationMap(locations),
    "INTERNAL",
  );

export const buildPopularInternationalGroups = (locations: HotelLocation[]) =>
  getPopularDestinationsDisplay(
    locations.map((location) => location._id),
    toLocationMap(locations),
    "EXTERNAL",
  );

export const groupDestinationsByCity = (
  primaryKeys: string[],
  data: Record<string, HotelLocation>
): GroupedDestination[] => {
  const groups: Record<string, GroupedDestination> = {};
  const groupOrder: string[] = [];

  primaryKeys.forEach((locationId) => {
    const location = data[locationId];
    if (!location) return;

    const key = getGroupKey(location);
    const cityId = getCityId(location);
    const cityName = getCityName(location);

    if (!groups[key]) {
      groups[key] = {
        key,
        cityId,
        cityName,
        totalHotels: 0,
        items: [],
      };
      groupOrder.push(key);
    }

    groups[key].items.push(location);
    groups[key].totalHotels = Math.max(groups[key].totalHotels, getHotelsCount(location));
  });

  return groupOrder.map((key) => {
    const group = groups[key];
    return {
      ...group,
      selectableLocation: buildCitySelectableLocation(group),
    };
  });
};
